<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import ResourceLanguageDropdown from '@/resources/ResourceLanguageDropdown.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useVisibilityStore } from '@/visibility/visibility.store'
import GoalSections from '../GoalSections.vue'
import { composeGoalText, goalSectionsLayout, type GoalSection } from '../goal_sections'
import TranslateButton from '../translation/TranslateButton.vue'
import type { TranslationLanguage } from '../translation/translation_types'
import { LOADING_ROW_WIDTHS } from '../objectives/objective_list'
import { useAnchorPageStore } from '../anchor_page.store'
import type { CopySource } from '../anchor_page_types'
import { useGoalResourcesStore } from '../goal_resources.store'
import { useAnchorPageTracking } from '../useAnchorPageTracking'

const LOADING_ROW_COUNT = 3
const TRANSLATE_SEPARATOR_CLASSES = 'ml-2 border-l border-gray-500 pl-2 text-text-xl leading-6'

const props = withDefaults(defineProps<{
  goalLabel: string
  isAccordionLayout?: boolean
  isTranslateButtonShown?: boolean
  translationLanguages?: TranslationLanguage[]
  isReadOnly?: boolean
  readOnlyHint?: string
  copySource?: CopySource
  isLibraryCopy?: boolean
}>(), {
  isAccordionLayout: false,
  isTranslateButtonShown: false,
  translationLanguages: () => [],
  isReadOnly: false,
  readOnlyHint: undefined,
  copySource: undefined,
  isLibraryCopy: false
})

const emit = defineEmits<{
  translate: [language: string]
  reset: []
}>()

const anchorPageStore = useAnchorPageStore()
const { content, customPrimaryGoalText, customGoalSections, isFetchingCustomization, accessInfo, strategyMap } = storeToRefs(anchorPageStore)
const { editorVisibility } = storeToRefs(useVisibilityStore())
const goalResourcesStore = useGoalResourcesStore()
const { editAnnualText } = useAnchorPageTracking(getAnalyticsTracker())

const contentUuid = computed(() => content.value?.uuid ?? '')
const copyTrackUuid = computed(() => props.copySource?.uuid ?? contentUuid.value)
const copyTrackTitle = computed(() => props.copySource?.title ?? content.value?.title)
const originalGoalText = computed(() => content.value?.goal_text ?? '')
const isCustomizedBadgeShown = computed(() => editorVisibility.value && Boolean(content.value?.customized))
// Vue 2's saved page showed a library copy's own text whatever the anchor page's blur rule said.
const isTextLocked = computed(() => accessInfo.value.blur && !props.isLibraryCopy)
const isEditorShown = computed(() => !isTextLocked.value && Boolean(customPrimaryGoalText.value))
const areSectionsShown = computed(() => !isTextLocked.value && Boolean(customGoalSections.value))
const originalGoalSections = computed(() => content.value?.goal_sections ?? [])
const sectionsLayout = computed(() => goalSectionsLayout(content.value?.goal_format))
const resourceLanguages = computed(() => goalResourcesStore.languagesById(contentUuid.value))
const isLanguageDropdownShown = computed(() => goalResourcesStore.hasNonEnglishLangById(contentUuid.value))
const selectedResourceLanguage = computed({
  get: () => goalResourcesStore.selectedLanguageById(contentUuid.value),
  set: (language: string) => goalResourcesStore.setSelectedLanguage(contentUuid.value, language)
})

function recordAnnualEdit(text: string, isBackToOriginal: boolean) {
  editAnnualText(text)
  if (isBackToOriginal) emit('reset')
}

function updateGoalText(text: string) {
  recordAnnualEdit(text, text === originalGoalText.value)
}

function updateGoalSections(sections: GoalSection[]) {
  anchorPageStore.setGoalSections(sections)
  const composedText = composeGoalText(sections)
  recordAnnualEdit(composedText, composedText === composeGoalText(originalGoalSections.value))
}
</script>

<template>
  <CopyTrackWrapper
    type="annual_target"
    level="Annual"
    :title="copyTrackTitle"
    :uuid="copyTrackUuid"
    :gate-copy="copySource?.isGated ?? true"
    :demo="!copySource && content?.demo"
    class="[&_p]:!mb-0"
    :class="{ 'px-4 py-2 font-regular': isAccordionLayout }"
    data-testid="annual-target-text"
  >
    <div
      v-if="isCustomizedBadgeShown"
      class="inline-block rounded-lg bg-gray-700 px-2 py-[3px] text-text-sm font-bold leading-none text-white"
      data-testid="customized-badge"
    >Customized</div>
    <div v-if="isFetchingCustomization" class="flex flex-col gap-2" data-testid="annual-target-loading">
      <LoadingSkeletonRow v-for="row in LOADING_ROW_COUNT" :key="row" :possible-widths="LOADING_ROW_WIDTHS" height="h-5" />
    </div>
    <GoalSections
      v-else-if="areSectionsShown"
      :key="contentUuid"
      :sections="customGoalSections ?? []"
      :original-sections="originalGoalSections"
      :layout="sectionsLayout"
      :strategies="strategyMap"
      :content-title="goalLabel"
      :is-read-only="isReadOnly"
      :read-only-hint="readOnlyHint"
      @update:sections="updateGoalSections"
    >
      <template v-if="isTranslateButtonShown" #toolbar>
        <span :class="TRANSLATE_SEPARATOR_CLASSES">
          <TranslateButton :languages="translationLanguages" @translation-language-changed="emit('translate', $event)" />
        </span>
      </template>
    </GoalSections>
    <WikiTextEditor
      v-else-if="isEditorShown"
      :key="contentUuid"
      format="markdown"
      :wiki-text="customPrimaryGoalText ?? ''"
      :reset-value="originalGoalText"
      :show-reset="!isLibraryCopy"
      :read-only="isReadOnly"
      :read-only-hint="readOnlyHint"
      :label="`${goalLabel} statement`"
      :strategies="strategyMap"
      copy-tooltip-text="Goal Copied"
      @update:wiki-text="updateGoalText"
    >
      <template v-if="isTranslateButtonShown" #bottomLeftContent>
        <span :class="TRANSLATE_SEPARATOR_CLASSES">
          <TranslateButton :languages="translationLanguages" @translation-language-changed="emit('translate', $event)" />
        </span>
      </template>
      <template v-if="isLanguageDropdownShown" #bottomRightContent>
        <ResourceLanguageDropdown v-model="selectedResourceLanguage" :languages="resourceLanguages" />
      </template>
    </WikiTextEditor>
  </CopyTrackWrapper>
</template>
